import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { first } from "rxjs/operators";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { EmergencyAccessAcceptRequest } from "@bitwarden/common/models/request/emergency-access-accept.request";
import { RouterService } from "@bitwarden/web-vault/app/core";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";

@Component({
  selector: "app-accept-emergency",
  templateUrl: "accept-emergency.component.html",
})
// eslint-disable-next-line rxjs-angular/prefer-takeuntil
export class AcceptEmergencyComponent implements OnInit {
  id: string;
  token: string;
  grantorName: string;
  grantorEmail: string;

  loading = false;
  error: string | null = null;
  isAuthenticated = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private stateService: StateService,
    private i18nService: I18nService,
    private platformUtilsService: PlatformUtilsService,
    private logService: LogService,
    private routerService: RouterService,
  ) {}

  ngOnInit() {
    // eslint-disable-next-line rxjs-angular/prefer-takeuntil, rxjs/no-async-subscribe
    this.route.queryParams.pipe(first()).subscribe(async (params) => {
      this.id = params["id"];
      this.token = params["token"];
      this.grantorName = params["name"];
      this.grantorEmail = params["email"];

      try {
        this.isAuthenticated = await this.stateService.getIsAuthenticated();
      } catch (e) {
        this.logService.error(String(e));
        this.error = this.i18nService.t("unexpectedError");
      }
    });
  }

  async accept() {
    this.loading = true;
    this.error = null;
    try {
      const request = new EmergencyAccessAcceptRequest();
      request.token = this.token;
      await this.apiService.postEmergencyAccessAccept(this.id, request);
      this.platformUtilsService.showToast(
        "success",
        "",
        this.i18nService.t("emergencyInviteAcceptedDesc"),
      );
      await this.navigateToEmergencyAccess();
    } catch (e) {
      this.logService.error(e);
      this.error = this.i18nService.t("emergencyInviteAcceptFailed");
    } finally {
      this.loading = false;
    }
  }

  navigateToLogin() {
    this.routerService.setPreviousUrl(this.router.url);
    this.router.navigate(["/login"]);
  }

  // The user layout's InitialRedirectGuard sends any first entry other than /views to /views, so
  // going straight from here to Emergency Access always ends up on /views. Enter the layout
  // through /views first: the guard doesn't run again for navigations within it. A failed
  // navigation only gets logged; the accept itself already succeeded.
  async navigateToEmergencyAccess() {
    try {
      if (await this.navigate(["/" + GlossRoutes.Views])) {
        await this.navigate(["/settings/security/emergency-access"]);
      }
    } catch (e) {
      this.logService.error(e);
    }
  }

  private async navigate(route: string[]): Promise<boolean> {
    const navigated = await this.router.navigate(route);
    if (navigated === false) {
      this.logService.warning(`Navigation to ${route.join("/")} was cancelled`);
    }
    return navigated !== false;
  }
}
